import { cx } from '../lib/format.js';
import { useTheme } from '../lib/useTheme.js';

/**
 * ThemeToggle — switches between light and dark.
 *
 * Implemented as a `role="switch"` button rather than a plain button, so assistive
 * technology announces both the control and its current state ("Dark mode,
 * switch, off") instead of just a label. The icon crossfades and rotates so the
 * change is legible at a glance rather than relying on colour alone.
 *
 * @param {{ variant?: 'default' | 'shell' }} props
 *   `shell` is for placement on the always-dark admin sidebar, where the normal
 *   theme-aware colours would be invisible.
 */
export default function ThemeToggle({ variant = 'default', className }) {
  const { isDark, toggleTheme } = useTheme();

  const palette =
    variant === 'shell'
      ? 'text-shell-muted hover:bg-shell-fg/10 hover:text-shell-fg'
      : 'text-ink-500 hover:bg-ink-100 hover:text-ink-900';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label="Dark mode"
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={toggleTheme}
      className={cx('relative grid size-9 place-items-center rounded-lg transition-colors', palette, className)}
    >
      {/* Both icons are always rendered and crossfaded, so the swap animates
          instead of popping. aria-hidden because the button already has a label
          and aria-checked conveys the state. */}
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className={cx(
          'absolute size-5 transition-all duration-300',
          isDark ? 'scale-50 rotate-90 opacity-0' : 'scale-100 rotate-0 opacity-100',
        )}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        {/* Sun */}
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
      </svg>

      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className={cx(
          'absolute size-5 transition-all duration-300',
          isDark ? 'scale-100 rotate-0 opacity-100' : 'scale-50 -rotate-90 opacity-0',
        )}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Moon */}
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
      </svg>
    </button>
  );
}
